/**
 * Cart Sale Bar - İndirim Barı Yönetimi
 * Sepet tutarına göre indirim barlarını dinamik olarak günceller
 */

class CartSaleBar {
  constructor() {
    this.container = document.querySelector('.cart-sale-bar');
    if (!this.container) return;

    this.heading = this.container.querySelector('.cart-sale-bar__heading-title');
    this.bars = this.container.querySelectorAll('.cart-sale-bar__bar');
    this.salesData = this.getSalesData();
    this.isCalculating = false;

    this.init();
  }

  /**
   * Schema'dan gelen indirim verilerini alır ve sıralar
   */
  getSalesData() {
    const data = [];

    this.bars.forEach(bar => {
      const price = parseFloat(bar.dataset.price);
      const percent = parseFloat(bar.dataset.percent) || null;
      const salePrice = bar.dataset.salePrice || null;

      if (price && (percent || salePrice)) {
        data.push({ price, percent, salePrice, element: bar });
      }
    });

    // Fiyata göre küçükten büyüğe sırala
    return data.sort((a, b) => a.price - b.price);
  }

  /**
   * Sepet tutarını alır (TL cinsinden)
   * Shopify'ın /cart.js endpoint'inden veri çeker
   */
  async getCartTotal() {
    try {
      // Shopify Cart API'den sepet bilgilerini al
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error('Cart API yanıt vermedi');
      }

      const cart = await response.json();

      // total_price kuruş cinsinden gelir, TL'ye çevir
      const totalInTL = cart.total_price / 100;

      return totalInTL;
    } catch (error) {
      console.error('Cart Sale Bar: Sepet tutarı alınamadı:', error);

      // Hata durumunda fallback: DOM'dan oku
      return this.getCartTotalFromDOM();
    }
  }

  /**
   * Yedek yöntem: DOM'dan sepet tutarını okur
   */
  getCartTotalFromDOM() {
    // data-cart-total attribute'unu kontrol et
    const cartTotal = this.container?.dataset.cartTotal;
    if (cartTotal) {
      return parseFloat(cartTotal) || 0;
    }

    // DOM'dan oku
    const cartTotalElement = document.querySelector('[data-hulkapps-cart-total]');
    if (!cartTotalElement) return 0;

    // Shopify para formatından sayıya çevir (örn: "5.700,00 TL" -> 5700)
    const totalText = cartTotalElement.textContent.trim();
    const numericValue = totalText
      .replace(/\./g, '') // Binlik ayırıcı noktaları kaldır
      .replace(/[^\d,]/g, '') // Sayı ve virgül dışındaki karakterleri kaldır
      .replace(',', '.'); // Ondalık virgülü noktaya çevir

    return parseFloat(numericValue) || 0;
  }

  /**
   * Fiyatı Türkçe formatında gösterir (örn: 5.700)
   */
  formatPrice(price) {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  /**
   * Her bir bar için doluluk yüzdesini hesaplar
   */
  calculateBarProgress(cartTotal, targetPrice, index) {
    // Eğer önceki bir hedef varsa, ondan sonrasını hesapla
    const previousPrice = index > 0 ? this.salesData[index - 1].price : 0;

    // Sepet bu seviyeye henüz ulaşmadıysa
    if (cartTotal < previousPrice) {
      return 0;
    }

    // Sepet bu seviyeyi geçtiyse
    if (cartTotal >= targetPrice) {
      return 100;
    }

    // Aradaysa yüzdeyi hesapla
    const progress = ((cartTotal - previousPrice) / (targetPrice - previousPrice)) * 100;
    return Math.min(100, Math.max(0, progress));
  }

  /**
   * Aktif seviyeyi ve kalan tutarı belirler
   */
  getActiveLevel(cartTotal) {
    // En yüksek seviyeyi geçti mi kontrol et
    const maxLevel = this.salesData[this.salesData.length - 1];
    if (cartTotal >= maxLevel.price) {
      return {
        completed: true,
        percent: maxLevel.percent,
        salePrice: maxLevel.salePrice,
        remaining: 0,
        nextPercent: null,
        nextSalePrice: null
      };
    }

    // Hangi seviyede olduğunu bul
    for (let i = 0; i < this.salesData.length; i++) {
      const level = this.salesData[i];

      if (cartTotal < level.price) {
        const remaining = level.price - cartTotal;
        const currentPercent = i > 0 ? this.salesData[i - 1].percent : 0;
        const currentSalePrice = i > 0 ? this.salesData[i - 1].salePrice : null;

        return {
          completed: false,
          percent: level.percent,
          salePrice: level.salePrice,
          remaining: remaining,
          nextPercent: level.percent,
          nextSalePrice: level.salePrice,
          currentPercent: currentPercent,
          currentSalePrice: currentSalePrice
        };
      }
    }

    return null;
  }

  /**
   * Heading metnini günceller
   */
  updateHeading(cartTotal) {
    if (!this.heading) return;

    const activeLevel = this.getActiveLevel(cartTotal);
    if (!activeLevel) return;

    if (activeLevel.completed) {
      // En yüksek seviyeye ulaşıldı
      // Eğer percent varsa yüzde göster, yoksa fiyat göster
      if (activeLevel.percent) {
        this.heading.innerHTML = `Tebrikler! %<span class="percent-bar">${activeLevel.percent}</span> indirim oranında indirim kazandınız! 🎉`;
      } else if (activeLevel.salePrice) {
        this.heading.innerHTML = `Tebrikler! <span class="percent-bar">${activeLevel.salePrice}</span> TL indirim kazandınız! 🎉`;
      }
    } else {
      // Henüz bir sonraki seviyeye ulaşılmadı
      const formattedRemaining = this.formatPrice(activeLevel.remaining);

      // Eğer percent varsa yüzde göster, yoksa fiyat göster
      if (activeLevel.nextPercent) {
        this.heading.innerHTML = `%<span class="percent-bar">${activeLevel.nextPercent}</span> indirim kazanmak için <span class="price-bar">${formattedRemaining}</span> TL'lik daha ürün ekle`;
      } else if (activeLevel.nextSalePrice) {
        this.heading.innerHTML = `<span class="percent-bar">${activeLevel.nextSalePrice}</span> TL indirim kazanmak için <span class="price-bar">${formattedRemaining}</span> TL'lik daha ürün ekle`;
      }
    }
  }

  /**
   * Bar görsellerini günceller
   * Barlar sırayla doldurulur (cascade effect)
   */
  updateBars(cartTotal) {
    if (!this.salesData || this.salesData.length === 0) return;

    this.salesData.forEach((sale, index) => {
      if (!sale.element) return;

      const progress = this.calculateBarProgress(cartTotal, sale.price, index);
      const barItem = sale.element.querySelector('.cart-sale-bar__bar-item');

      if (barItem) {
        // Her bar için sıralı gecikme (cascade effect)
        // İlk bar hemen, ikinci 200ms sonra, üçüncü 400ms sonra vb.
        setTimeout(() => {
          // CSS değişkenini güncelle (animasyonlu geçiş için)
          sale.element.style.setProperty('--percent-loading', `${progress}%`);

          // Aktif durumu belirle
          if (progress > 0 && progress < 100) {
            sale.element.classList.add('is-active');
          } else {
            sale.element.classList.remove('is-active');
          }

          // Tamamlanmış durumu
          if (progress === 100) {
            sale.element.classList.add('is-completed');
          } else {
            sale.element.classList.remove('is-completed');
          }
        }, index * 200); // Her bar 200ms arayla doldurulur
      }
    });
  }

  /**
   * Tüm bileşenleri günceller
   */
  async update() {
    // Çoklu güncelleme çağrılarını engelle
    if (this.isCalculating) return;

    this.isCalculating = true;
    this.showCalculating();

    // DOM yeniden render edilmiş olabilir, referansları yenile
    this.refreshDOMReferences();

    const cartTotal = await this.getCartTotal();

    // Container'ın data-cart-total attribute'unu güncelle
    if (this.container) {
      this.container.dataset.cartTotal = cartTotal;
    }

    this.updateBars(cartTotal);
    this.updateHeading(cartTotal);

    // Loading durumunu kaldır
    this.hideCalculating();
    this.isCalculating = false;
  }

  /**
   * "Hesaplanıyor..." mesajını ve shimmer efektini gösterir
   */
  showCalculating() {
    if (!this.heading) return;
    if (this.container) {
      this.container.classList.add('is-loading');
    }
    this.heading.textContent = 'İndirim hesaplanıyor...';
  }

  /**
   * Loading durumunu kaldırır
   */
  hideCalculating() {
    if (this.container) {
      this.container.classList.remove('is-loading');
    }
  }

  /**
   * DOM referanslarını yeniler (sepet yeniden render edildiyse)
   */
  refreshDOMReferences() {
    // Container'ı yeniden bul
    const newContainer = document.querySelector('.cart-sale-bar');
    if (!newContainer) return;

    // Eğer container değiştiyse (yeniden render edildiyse) referansları güncelle
    if (this.container !== newContainer) {
      this.container = newContainer;
    }

    // Her zaman güncel referansları al
    this.heading = this.container.querySelector('.cart-sale-bar__heading-title');
    this.bars = this.container.querySelectorAll('.cart-sale-bar__bar');
    this.salesData = this.getSalesData();
  }

  /**
   * Sepet değişikliklerini dinler
   * Theme'in cart.js ve cart-change.js event'lerini kullanır
   */
  observeCartChanges() {
    // 1. EN ÖNEMLİ: cart:rerendered event'ini dinle
    // theme.js satır 2188'de cart DOM'u yeniden render edildikten SONRA tetiklenir
    document.addEventListener('cart:rerendered', () => {
      // Küçük bir gecikme ile güncelle (DOM'un settle olmasını bekle)
      setTimeout(() => {
        this.update();
      }, 50);
    });

    // 2. Shopify Section API event'leri (theme editor için)
    document.addEventListener('shopify:section:load', () => {
      this.update();
    });
  }


  /**
   * İlk başlatma
   */
  init() {
    if (this.salesData.length === 0) {
      console.warn('Cart Sale Bar: Hiç indirim verisi bulunamadı.');
      return;
    }

    // İlk yükleme
    this.update();

    // Sepet değişikliklerini dinlemeye başla
    this.observeCartChanges();
  }
}

// Sayfa yüklendiğinde başlat
document.addEventListener('DOMContentLoaded', () => {
  new CartSaleBar();
});

// Shopify theme editor için
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.cart-sale-bar')) {
    new CartSaleBar();
  }
});
